import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase-config";
import { collection, addDoc, serverTimestamp, getDocs, where, query as fsQuery } from "firebase/firestore";
import loginBg from '../assets/svg/login.svg';
import "./EmployeeDashboard.css";

const absenceTypes = [
  "Congé Payé",
  "Indisponibilité Ponctuelle",
  "Congé sans solde",
  "Évènement familial"
];
const dayParts = ["Matin", "Après-midi"];

function EmployeeAbsenceNew() {
  const navigate = useNavigate();
  const [type, setType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [startPart, setStartPart] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endPart, setEndPart] = useState("");
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [employeeName, setEmployeeName] = useState("");

  useEffect(() => {
    const fetchName = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const q = fsQuery(collection(db, "employees"), where("email", "==", user.email));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const data = snap.docs[0].data();
        setEmployeeName(`${data.prenom} ${data.nom}`);
      }
    };
    fetchName();
  }, []);

  const isValid = type && startDate && startPart && endDate && endPart && !sending;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isValid) return;
    
    // Validation supplémentaire
    if (!type || !startDate || !startPart || !endDate || !endPart) {
      setError("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    setSending(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Utilisateur non connecté");
      
      // Préparer les données avec validation
      const absenceData = {
        employeeEmail: user.email,
        employeeName: employeeName || user.email, // Fallback si employeeName est vide
        type,
        startDate,
        startPart,
        endDate,
        endPart,
        comment: comment || "", // S'assurer que comment n'est pas undefined
        status: "En attente",
        createdAt: serverTimestamp(),
        vuParAdmin: false, // Ajouter ce champ manquant
        vuParEmploye: false // Ajouter ce champ manquant
      };
      
      console.log("📝 Données d'absence à envoyer:", absenceData);
      console.log("👤 Utilisateur actuel:", user.email);
      
      await addDoc(collection(db, "absences"), absenceData);
      console.log("✅ Demande d'absence envoyée avec succès");
      navigate("/absence-request");
    } catch (err) {
      console.error("❌ Erreur lors de l'envoi de la demande d'absence:", err);
      setError("Erreur lors de l'envoi : " + err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="dashboard-board-container" style={{
      minHeight: '100vh',
      background: `url(${loginBg}) center/cover no-repeat`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0
    }}>
      <div className="dashboard-board-content" style={{
        background: 'rgba(255,255,255,0.95)',
        borderRadius: 20,
        padding: 24,
        boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
        minWidth: 320,
        maxWidth: 400,
        minHeight: 420,
        textAlign: 'center',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center'
      }}>
        {/* Croix de fermeture */}
        <button onClick={() => navigate("/absence-request")}
          style={{position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', fontSize: 28, cursor: 'pointer', color: '#333', width: 36, height: 36, lineHeight: 1, zIndex: 2}}
          aria-label="Fermer"
        >
          ×
        </button>
        <h2 style={{marginBottom: 24}}>Nouvelle demande d'absence</h2>
        <form onSubmit={handleSubmit} style={{width: '100%', maxWidth: 340, margin: '0 auto'}}>
          <div style={{marginBottom: 18, textAlign: 'left'}}>
            <label style={{fontWeight: 500}}>Type d'absence</label>
            <select value={type} onChange={e => setType(e.target.value)} style={{width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ccc', marginTop: 6}} required>
              <option value="">Sélectionner</option>
              {absenceTypes.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div style={{marginBottom: 18, textAlign: 'left'}}>
            <label style={{fontWeight: 500}}>Date de début</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ccc', marginTop: 6}} required />
          </div>
          <div style={{marginBottom: 18, textAlign: 'left'}}>
            <label style={{fontWeight: 500}}>Partie de la journée (début)</label>
            <select value={startPart} onChange={e => setStartPart(e.target.value)} style={{width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ccc', marginTop: 6}} required>
              <option value="">Sélectionner</option>
              {dayParts.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div style={{marginBottom: 18, textAlign: 'left'}}>
            <label style={{fontWeight: 500}}>Date de fin</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ccc', marginTop: 6}} required />
          </div>
          <div style={{marginBottom: 18, textAlign: 'left'}}>
            <label style={{fontWeight: 500}}>Partie de la journée (incluse pour la fin)</label>
            <select value={endPart} onChange={e => setEndPart(e.target.value)} style={{width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ccc', marginTop: 6}} required>
              <option value="">Sélectionner</option>
              {dayParts.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div style={{marginBottom: 18, textAlign: 'left'}}>
            <label style={{fontWeight: 500}}>Commentaire (optionnel)</label>
            <textarea value={comment} onChange={e => setComment(e.target.value)} maxLength={250} style={{width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ccc', marginTop: 6, minHeight: 60, resize: 'vertical'}} placeholder="Votre message (250 caractères max)" />
          </div>
          {error && <div style={{color: 'red', marginBottom: 12}}>{error}</div>}
          <button type="submit" className="absence" style={{width: '100%', fontSize: 18, marginTop: 10}} disabled={!isValid}>
            {sending ? 'Envoi en cours...' : 'Envoyer la demande'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default EmployeeAbsenceNew; 